import { phoneCategory } from "../../supabase/functions/_shared/phoneCategory";
import type { Category, Listing, ListingCondition, TitleSuggestionRule } from "../types/domain";

const CONDITION_NOT_APPLICABLE_CATEGORY_SLUGS = new Set(["education-courses"]);

type CategorySlug = Pick<Category, "slug"> | null | undefined;

export function childrenOf(categories: Category[], parentId: number | null): Category[] {
  return categories
    .filter((category) => category.parent_id === parentId)
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, "ru"));
}

export function categoryPath(categories: Category[], categoryId: number | null): Category[] {
  if (!categoryId) return [];
  const byId = new Map(categories.map((category) => [category.id, category]));
  const path: Category[] = [];
  let cursor = byId.get(categoryId) ?? null;
  while (cursor) {
    path.unshift(cursor);
    cursor = cursor.parent_id ? byId.get(cursor.parent_id) ?? null : null;
  }
  return path;
}

export function isLeafCategory(categories: Category[], categoryId: number | null): boolean {
  if (!categoryId) return false;
  return !categories.some((category) => category.parent_id === categoryId);
}

export function conditionLabel(value: ListingCondition | string | null | undefined): string {
  if (value === "new") return "Новое";
  if (value === "like_new") return "Как новое";
  return "Б/у";
}

export function isConditionApplicableForCategories(categories: readonly CategorySlug[]): boolean {
  return !categories.some((category) => category?.slug && CONDITION_NOT_APPLICABLE_CATEGORY_SLUGS.has(category.slug));
}

export function isConditionApplicableToListing(
  listing: Pick<Listing, "category" | "subcategory" | "leaf_category"> | null | undefined,
): boolean {
  if (!listing) return true;
  return isConditionApplicableForCategories([listing.category, listing.subcategory, listing.leaf_category]);
}

function normalizeSearch(value: string): string {
  return value.toLowerCase().replace(/ё/g, "е").trim();
}

function unique(items: string[]): string[] {
  return Array.from(new Set(items.map((item) => item.trim()).filter(Boolean)));
}

const conditionSuggestionValues = new Set(["б/у", "бу", "б у", "новое", "новый", "новая", "новые", "как новое", "как новый", "как новая"]);

export function isConditionTitleSuggestion(value: string): boolean {
  return conditionSuggestionValues.has(normalizeSearch(value).replace(/[./_-]+/g, " ").replace(/\s+/g, " "));
}

export function filterTitleSuggestions(items: string[]): string[] {
  return unique(items).filter((item) => !isConditionTitleSuggestion(item));
}

export function titleSuggestions(title: string, categoryPathNames: string[]): string[] {
  const source = normalizeSearch(`${title} ${categoryPathNames.join(" ")}`);
  const suggestions: string[] = [];

  if (/кроссов/.test(source)) {
    suggestions.push("для мотокросса", "fox", "ataki", "xxl", "белый", "новый", "эндуро");
  }
  if (/эндуро/.test(source)) {
    suggestions.push("для эндуро", "с визором", "с защитой", "размер L", "размер XL");
  }
  if (/детск/.test(source)) {
    suggestions.push("для мальчика", "для девочки", "размер S", "лёгкий", "регулируемый");
  }
  if (/женск/.test(source)) {
    suggestions.push("женский", "размер M", "белый", "чёрный", "лёгкий");
  }
  if (/интеграл/.test(source)) {
    suggestions.push("интегральный", "с визором", "pinlock", "размер L", "чёрный");
  }
  if (/шлем|каск|helmet/.test(source)) {
    suggestions.push("для мотоцикла", "кроссовый", "детский", "женский", "интеграл", "эндуро");
  }
  if (/мото|скутер|мопед|байк/.test(source)) {
    suggestions.push("для мотоцикла", "для мотокросса", "мотоциклетный", "защитный", "без кожи");
  }
  if (/эспанд|экспанд|тренаж.*рук|кистев/.test(source)) {
    suggestions.push("для рук", "кистевой", "регулируемый", "для тренировок");
  }
  if (/гантел/.test(source)) suggestions.push("5 кг", "10 кг", "разборная", "пара");
  if (/науш|аудио|bluetooth/.test(source)) suggestions.push("Bluetooth", "с шумоподавлением", "комплект");
  if (/сумк|рюкзак/.test(source)) suggestions.push("эко-кожа", "оливковая", "без кожи", "через плечо");
  if (/керами|посуд|чашк|тарел/.test(source)) suggestions.push("ручная работа", "набор", "для чаепития");
  if (/квартир|комнат|дом|дач/.test(source)) suggestions.push("без посредников", "с ремонтом", "рядом с метро");
  if (/гараж|паркинг|машиномест/.test(source)) suggestions.push("собственник", "охраняемый", "рядом с домом", "сухой");
  if (/работ|ваканси|подработ/.test(source)) suggestions.push("удалённо", "частичная занятость", "без опыта");
  if (/постоян|сотрудник/.test(source)) suggestions.push("полная занятость", "официально", "опыт не обязателен");
  if (/ремонт|уборк|доставк|обучен|консультац/.test(source)) suggestions.push("выезд", "онлайн", "по договорённости");
  if (/васту/.test(source)) suggestions.push("консультация", "разбор пространства", "онлайн");
  if (/астролог|натальн|гороскоп/.test(source)) suggestions.push("натальная карта", "личная консультация", "онлайн");
  if (/юрист|юридич|договор/.test(source)) suggestions.push("документы", "разовая консультация", "онлайн");
  if (/писание|вед|бхагавад|гита|сутр/.test(source)) suggestions.push("священное писание", "твёрдый переплёт", "в хорошем состоянии");
  if (/мурти|статуэт|благовон|четк|чётк|мала|алтар/.test(source)) suggestions.push("для практики", "ручная работа", "натуральные материалы");

  return filterTitleSuggestions([...suggestions, "ручная работа", "без кожи"]).slice(0, 8);
}

export function titleSuggestionsFromRules(title: string, rules: TitleSuggestionRule[]): string[] {
  const source = normalizeSearch(title);
  if (source.length < 2) return [];
  return filterTitleSuggestions(
    rules
      .filter((rule) => rule.is_active && source.includes(normalizeSearch(rule.trigger)))
      .sort((a, b) => b.priority - a.priority || b.trigger.length - a.trigger.length)
      .flatMap((rule) => rule.suggestions),
  ).slice(0, 8);
}

export function formatCategoryPath(categories: Category[], categoryId: number): string {
  return categoryPath(categories, categoryId).map((category) => category.name).join(" › ");
}

function leafDescendants(categories: Category[], category: Category): Category[] {
  const children = childrenOf(categories, category.id);
  if (!children.length) return [category];
  return children.flatMap((child) => leafDescendants(categories, child));
}

function hasAutomotiveContext(value: string): boolean {
  const query = normalizeSearch(value);
  return /авто|автомоб|машин|\bbmw\b|\bkia\b|carnival|toyota|hyundai|nissan|renault|volkswagen|mercedes|skoda|lada|лада|ваз/.test(query);
}

export function isAutomotiveAccessoryTitle(value: string): boolean {
  const query = normalizeSearch(value);
  return hasAutomotiveContext(query) && /коврик|чехол|багажник|салон|дворник|щетк|держател|зарядк/.test(query);
}

export function categorySuggestions(title: string, categories: Category[], currentCategoryId: number | null): Category[] {
  const query = normalizeSearch(title);
  if (query.length < 2) return [];

  const phone = phoneCategory(title, categories);
  if (phone) return [phone];

  const slugGroups: string[][] = [];
  const automotiveContext = hasAutomotiveContext(query);
  const automotiveAccessoryContext = isAutomotiveAccessoryTitle(query);
  if (/шлем|каск|защит|экип/.test(query)) {
    slugGroups.push(["equipment"]);
  }
  if (/мото|скутер|мопед|байк|эндуро|кросс/.test(query)) {
    slugGroups.push(["equipment"], ["motorcycles", "scooters", "all-terrain-vehicles"], ["parts-accessories"]);
  }
  if (automotiveAccessoryContext) slugGroups.push(["vehicle-accessories", "parts-accessories"]);
  else if (automotiveContext) slugGroups.push(["cars-used", "cars-new", "parts-accessories"]);
  if (/сумк|рюкзак/.test(query)) slugGroups.push(["bags", "eco-bags"]);
  if (/одеж|куртк|плать|рубаш/.test(query)) slugGroups.push(["clothes", "kids-clothes"]);
  if (/обув|кроссов|ботин/.test(query)) slugGroups.push(["shoes", "kids-clothes"]);
  if (/науш/.test(query)) slugGroups.push(["headphones"]);
  if (/компьют|ноут/.test(query)) slugGroups.push(["computers"]);
  if (/эспанд|экспанд|тренаж.*рук|кистев/.test(query)) slugGroups.push(["hand-expanders", "fitness"]);
  if (/гантел/.test(query)) slugGroups.push(["dumbbells", "fitness"]);
  if (/коврик/.test(query) && !automotiveContext) slugGroups.push(["yoga-mats", "fitness"]);
  if (/тренаж/.test(query)) slugGroups.push(["fitness"]);
  if (/гараж|паркинг|машиномест/.test(query)) slugGroups.push(["garage", "parking-space", "parking"]);
  if (/квартир|комнат|дом|дач/.test(query)) slugGroups.push(["apartments-sale", "apartments-rent", "rooms", "houses-sale", "dachas"]);
  if (/постоян|ваканси|сотрудник|работ/.test(query)) slugGroups.push(["full-time-jobs", "remote-work", "part-time"]);
  if (/ремонт|уборк|клининг|доставк|перевоз|консультац|обучен|строител/.test(query)) {
    slugGroups.push(["renovation-finishing", "cleaning-services", "transport-delivery-services", "consulting-services", "education-courses", "construction-services"]);
  }
  if (/васту/.test(query)) slugGroups.push(["vastu-consulting", "consulting-services"]);
  if (/астролог|натальн|гороскоп/.test(query)) slugGroups.push(["astrology-consulting", "consulting-services"]);
  if (/юрист|юридич|правов|договор/.test(query)) slugGroups.push(["legal-consulting", "legal-services"]);
  if (/книг|литератур|писание|вед|бхагавад|гита|библи|коран|сутр/.test(query)) {
    slugGroups.push(["sacred-scriptures", "spiritual-literature", "philosophy", "literature-other-genres"]);
  }
  if (/мурти|статуэт|благовон|четк|чётк|мала|алтар|лампад|поющ|кристалл/.test(query)) {
    slugGroups.push(["murti-statues", "incense", "mala-beads", "altars-stands", "singing-bowls-bells", "crystals-minerals"]);
  }
  if (/свеч|мыл|вяз|керамик|декор|ручн/.test(query)) {
    slugGroups.push(["candles", "handmade-soap-cosmetics", "handmade-knitwear", "handmade-ceramics-dishes", "handmade-decor", "custom-handmade"]);
  }

  const bySlug = new Map(categories.map((category) => [category.slug, category]));
  const scored = new Map<number, number>();

  slugGroups.flat().forEach((slug, index) => {
    const category = bySlug.get(slug);
    if (!category) return;
    leafDescendants(categories, category).forEach((leaf) => {
      scored.set(leaf.id, Math.max(scored.get(leaf.id) ?? 0, 100 - index));
    });
  });

  categories.forEach((category) => {
    const path = normalizeSearch(formatCategoryPath(categories, category.id));
    const words = query.split(/\s+/).filter((word) => word.length >= 3);
    const score = words.reduce((sum, word) => sum + (path.includes(word) ? 12 : 0), 0);
    if (score) {
      leafDescendants(categories, category).forEach((leaf) => {
        scored.set(leaf.id, Math.max(scored.get(leaf.id) ?? 0, score));
      });
    }
  });

  if (currentCategoryId) {
    const current = categories.find((category) => category.id === currentCategoryId);
    if (current) leafDescendants(categories, current).forEach((leaf) => scored.set(leaf.id, Math.max(scored.get(leaf.id) ?? 0, 20)));
  }

  return Array.from(scored.entries())
    .map(([id, score]) => ({ category: categories.find((item) => item.id === id), score }))
    .filter((item): item is { category: Category; score: number } => Boolean(item.category))
    .filter((item) => !automotiveAccessoryContext || formatCategoryPath(categories, item.category.id).startsWith("Запчасти и аксессуары"))
    .sort((a, b) => b.score - a.score || formatCategoryPath(categories, a.category.id).localeCompare(formatCategoryPath(categories, b.category.id), "ru"))
    .map((item) => item.category)
    .slice(0, 6);
}
